import { type VipSignalCode } from "./researchCard";

/**
 * U2 VIP (Agent Brains 5.1: "the VIP flag rides the lead card; vip.detected pings
 * the owner"; Master Spec B2: VIP is "always the inference class,
 * confidence-tagged, evidence-kinded", "Prohibited permanently (T3): any price
 * or quote differentiated by inferred wealth"; CLAUDE.md sections 3 and 6).
 *
 * The deterministic half of VIP detection. Pure: signal codes in, a confidence
 * and a decision out. No model grades anything here (section 4), and nothing in
 * this file reads or writes a price.
 *
 * WHAT A FLAG CHANGES: routing and service level, never a number. This file
 * decides only whether the system RAISES the flag. The rules, each pinned by
 * vipSignal.test.ts:
 *  - Detection only ever raises. No decision here clears a flag.
 *  - It never overrides an owner's latest "off".
 *  - Below the bar it decides nothing about the contact: a computed low result
 *    is not a known "not VIP" (TA-216 decisions 1 to 3 stay held until Kabir
 *    rules), so the writer is never called for it.
 *  - `vip.owner_history_top` needs the price band table (design D34, unruled),
 *    so no caller can produce it yet. It is in the catalog so the weights
 *    table is the whole vocabulary.
 *
 * The signal codes are U1's closed catalog, `VIP_SIGNAL_CODES` in
 * `researchCard.ts` (U integration: U2 restated the list while U1 was built
 * in parallel; the two were identical and the copy is gone). The weights
 * table is keyed by that type, so a code added to the catalog without a
 * weight here does not compile.
 */

/**
 * AWAITING RATIFICATION, every number (owner interview). T2 thresholds: no
 * learner writes them in U. Placeholders from the U2 design.
 */
export const VIP_SIGNAL_WEIGHTS: Readonly<Record<VipSignalCode, number>> = Object.freeze({
  "vip.ig_verified": 0.5,
  "vip.followers_10k_100k": 0.2,
  "vip.followers_100k_1m": 0.5,
  "vip.followers_gte_1m": 0.7,
  "vip.follows_business": 0.05,
  "vip.press_mention": 0.4,
  "vip.owner_history_top": 0.6,
});

/** AWAITING RATIFICATION. The confidence at or above which the auto-flag may raise. */
export const VIP_FLAG_MIN_CONFIDENCE = 0.6;

/** Six decimals: the same signals always give the same number, with no float drift in a stored value. */
const round6 = (n: number): number => Math.round(n * 1_000_000) / 1_000_000;

/**
 * Noisy-or over the distinct signals: 1 - prod(1 - w). Deterministic, order
 * and duplicates do not matter, monotone in the signal set. An unknown code
 * throws: an unrecognised signal ignored would be a silently wrong number.
 */
export function vipSignalConfidence(signals: readonly VipSignalCode[]): number {
  let miss = 1;
  for (const code of new Set(signals)) {
    // AWAITING RATIFICATION: D34 (the price band table) is unruled, so this code
    // is not a signal the system may act on. Its weight alone clears the bar and
    // would raise a flag from a source that does not exist yet: refuse loudly.
    if (code === "vip.owner_history_top") {
      throw new Error("VIP signal vip.owner_history_top refused: it needs the price band table (D34), which is not ruled");
    }
    const w = Object.hasOwn(VIP_SIGNAL_WEIGHTS, code) ? VIP_SIGNAL_WEIGHTS[code] : undefined;
    if (w === undefined) throw new Error(`unknown VIP signal code "${String(code).slice(0, 64)}"`);
    miss *= 1 - w;
  }
  return round6(1 - miss);
}

export type VipHoldReason = "below_bar" | "no_signals" | "owner_set_off" | "already_vip" | "flag_off";

export type VipDecision =
  | { readonly kind: "raise"; readonly confidence: number }
  | { readonly kind: "hold"; readonly reason: VipHoldReason };

export interface DecideVipInput {
  readonly signals: readonly VipSignalCode[];
  /** The contact's flag as it stands. */
  readonly currentFlag: boolean;
  /** The direction of the OWNER's latest tag, or null when the owner never tagged. */
  readonly ownerLatest: "on" | "off" | null;
  /** ACKRA_VIP_AUTOFLAG. */
  readonly autoflagEnabled: boolean;
}

/**
 * The order is the precedence. No signals and below the bar come first: they
 * are about the evidence, and a weak read raises nothing whatever else is true.
 * Then the state that already answers it (flag stands), then the owner's
 * "off", then the switch.
 */
export function decideVip(i: DecideVipInput): VipDecision {
  if (i.signals.length === 0) return { kind: "hold", reason: "no_signals" };
  const confidence = vipSignalConfidence(i.signals);
  if (confidence < VIP_FLAG_MIN_CONFIDENCE) return { kind: "hold", reason: "below_bar" };
  if (i.currentFlag) return { kind: "hold", reason: "already_vip" };
  if (i.ownerLatest === "off") return { kind: "hold", reason: "owner_set_off" };
  if (!i.autoflagEnabled) return { kind: "hold", reason: "flag_off" };
  return { kind: "raise", confidence };
}
